import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, MarkdownEditor } from "@octopusdeploy/design-system-components";
import { ArrowLeftIcon } from "@octopusdeploy/design-system-icons";
import { colorScales, fontSize, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation, useAggregateAPIOperationStatus, useQuery } from "@octopusdeploy/octopus-react-client";
import type { GitRef, ProjectTemplateResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import classNames from "classnames";
import { useState } from "react";
import { IconAndLogoSelectorPopover } from "~/areas/projects/components/Projects/IconAndLogoSelectorPopover";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import InternalLink from "~/components/Navigation/InternalLink";
import { GuidedSetupFrame } from "~/components/OnboardingDialog/GuidedSetupFrame";
import addProjectImageDark from "~/components/ProjectBasedActivation/assets/img-addproject-dark.svg";
import addProjectImageLight from "~/components/ProjectBasedActivation/assets/img-addproject-light.svg";
import { required, Text } from "~/components/form";
import type { LogoEditorSettings } from "~/components/form";
import type { ProjectTemplateStatus } from "./ProjectTemplateStatus";
import { ProjectTemplateStatusBar } from "./ProjectTemplateStatus";
interface CreateNewProjectTemplateProps {
    gitRef: GitRef;
    next: (projectTemplate: ProjectTemplateResource) => void;
    close: () => void;
    projectTemplateStatus: ProjectTemplateStatus;
}
const DefaultProjectTemplateIconId = "layer-group";
const DefaultProjectTemplateIconColor = colorScales.grey[500];
function CreateNewProjectTemplate({ gitRef, next, close, projectTemplateStatus }: CreateNewProjectTemplateProps) {
    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const [iconId, setIconId] = useState<string>(DefaultProjectTemplateIconId);
    const [iconColor, setIconColor] = useState<string>(DefaultProjectTemplateIconColor);
    const [logo, setLogo] = useState<LogoEditorSettings>({ file: undefined, reset: false });
    const { result: iconSvgResources } = useQuery((repository) => repository.Icons.getIcons(), [], "Get Icons");
    const { result: iconMetadata } = useQuery((repository) => repository.Icons.getIconMetadata(), [], "Get Icon Metadata");
    const { execute: addProjectTemplate } = useMutation({
        name: "Create Project Template",
        action: async (repository) => {
            const projectTemplate = await repository.ProjectTemplates.add({
                Name: name,
                Description: description,
                Icon: iconId
                    ? {
                        Id: iconId,
                        Color: iconColor,
                    }
                    : undefined,
                ChangeDescription: `Create Project Template '${name}'`,
            }, gitRef);
            return projectTemplate;
        },
        afterComplete: async (_, output) => {
            next(output);
        },
    });
    const { isInProgress, errors } = useAggregateAPIOperationStatus();
    const hasMissingValues = !name;
    const handleSave = async () => {
        await addProjectTemplate();
        return true;
    };
    const helpPanelContent = (<div>
            <strong>Project Templates</strong> are used for standardization of projects and their dependencies to enforce structure and best practices in your deployments and Runbooks.
        </div>);
    return (<GuidedSetupFrame helpPanelImage={{
            src: addProjectImageLight,
            altText: "Add New Project Template",
            darkThemeSrc: addProjectImageDark,
        }} helpPanelContent={helpPanelContent} onClose={close} showThemeSelector={false} closeButtonTooltip="Close project template setup">
            <div className={styles.statusContainer}>
                <InternalLink className={styles.backLink} to={links.projectTemplatesPage.generateUrl({ gitRef })} onClick={(e) => {
            e.preventDefault();
            close();
        }}>
                    <ArrowLeftIcon size={20}/>
                    <span>Project Templates</span>
                </InternalLink>
                <ProjectTemplateStatusBar gitRef={gitRef} state={projectTemplateStatus.state} dispatch={projectTemplateStatus.dispatch} hideCloseButton/>
            </div>
            <div className={styles.templateContainer}>
                <SaveDialogLayout title={<span className={styles.title}>Add New Project Template</span>} busy={isInProgress} errors={errors} saveButtonLabel="Create Project Template" busyButtonLabel="Creating Project Template..." saveButtonDisabled={hasMissingValues} onSaveClick={handleSave} cancelButtonLabel="Cancel" onCancelClick={() => {
            close();
            return true;
        }}>
                    <div className={classNames({ [styles.templateNameContainer]: true, [styles.templateError]: errors !== undefined })}>
                        {iconSvgResources && iconMetadata && (<IconAndLogoSelectorPopover icons={iconSvgResources} iconMetadata={iconMetadata} iconId={iconId} iconColor={iconColor} logo={logo} onIconIdChange={(newIconId) => {
                setIconId(newIconId);
                setLogo({ ...logo, file: undefined });
            }} onIconColorChange={(newIconColor) => {
                setIconColor(newIconColor);
                setLogo({ ...logo, file: undefined });
            }} onLogoChange={(newLogo) => {
                setLogo(newLogo);
                setIconId(DefaultProjectTemplateIconId);
                setIconColor(DefaultProjectTemplateIconColor);
            }}/>)}
                        <Text label="Project template name" value={name} onChange={setName} validate={required("Please enter a project template name")} autoFocus={true}/>
                    </div>

                    {showAdvanced && (<>
                            <div className={styles.advancedSettingsLabel}>Advanced settings</div>
                            <div className={styles.descriptionContainer}>
                                <MarkdownEditor value={description} label="Description" onChange={setDescription}/>
                            </div>
                        </>)}

                    <div className={styles.advancedButtonContainer}>
                        <ActionButton label={showAdvanced ? "Hide advanced" : "Show advanced"} type={ActionButtonType.Ternary} onClick={(e) => {
            e.preventDefault();
            setShowAdvanced(!showAdvanced);
        }}/>
                    </div>
                </SaveDialogLayout>
            </div>
        </GuidedSetupFrame>);
}
const styles = {
    statusContainer: css({
        paddingTop: space[40],
    }),
    backLink: css({
        paddingBottom: space[16],
        display: "flex",
        font: text.body.regular.small,
        gap: space[4],
    }),
    templateContainer: css({
        flexBasis: "100%",
        "& .MuiDialogContent-root": {
            padding: "0 !important",
            overflow: "visible !important",
        },
        "& .MuiDialogActions-root, & .MuiDialogTitle-root.MuiTypography-root div": {
            padding: "0 !important",
        },
        "& .MuiDialogContent-root div[role='alert'] + div": {
            marginTop: space[32],
        },
        "& .MuiDialogTitle-root.MuiTypography-root": {
            padding: `${space[48]} 0 ${space[32]} 0 !important`,
            "& .MuiLinearProgress-root[role='progressbar']": {
                display: "none",
            },
        },
    }),
    title: css({
        overflow: "hidden",
        textOverflow: "ellipsis",
        font: text.heading.large,
    }),
    templateNameContainer: css({
        display: "flex",
    }),
    templateError: css({
        marginTop: space[32],
    }),
    advancedSettingsLabel: css({
        color: themeTokens.color.text.tertiary,
        textTransform: "uppercase",
        marginTop: space[32],
        marginBottom: space[16],
        fontSize: fontSize["xSmall"],
        fontWeight: fontWeight[700],
    }),
    descriptionContainer: css({
        padding: space[2],
        marginBottom: space[32],
    }),
    advancedButtonContainer: css({
        marginBottom: space[32],
    }),
};
export default CreateNewProjectTemplate;
